import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {debounceTime, firstValueFrom, Subject, switchMap, Subscription} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import { OrganizationModel } from '../../../../core/models/resources/organization.model';
import { NavigationService } from '../../../../service/services/misc/navigation/navigation.service';
import { FileUploadService } from '../../../../service/services/misc/file-upload/file-upload.service';
import { NotificationsService } from '../../../../service/services/misc/notifications/notifications.service';
import { Const, PolicyAction, ResourceState, ResourceType } from '../../../../core/helpers/Const';
import { Utils } from '../../../../core/helpers/Utils';
import { AiAssistantService } from '../../../../service/services/misc/ai-assistant/ai-assistant.service';
import {RuleModel} from '../../../../core/models/misc/rule.model';
import {GVTModel} from '../../../../core/models/misc/references.model';
import {ReconcileFailedErrorMessageDialogComponent} from '../../dialogs/reconcile-failed-error-message-dialog/reconcile-failed-error-message-dialog.component';
import {DeleteConfirmationDialogComponent} from '../../dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component';
import {CreateGenericResourceDialogComponent} from '../../dialogs/create-generic-resource-dialog/create-generic-resource-dialog.component';
import {CreateInstanceDialogComponent} from '../../dialogs/create-instance-dialog/create-instance-dialog.component';
import {CreateFolderDialogComponent} from '../../dialogs/create-folder-dialog/create-folder-dialog.component';
import {CreateResourceGroupDialogComponent} from '../../dialogs/create-resource-group-dialog/create-resource-group-dialog.component';
import {MoveResourceDialogComponent} from '../../dialogs/move-resource-dialog/move-resource-dialog.component';
import {MoveResourceConfirmationDialogComponent} from '../../dialogs/move-resource-confirmation-dialog/move-resource-confirmation-dialog.component';
import {MoveFailedErrorMessageDialogComponent} from '../../dialogs/move-failed-error-message-dialog/move-failed-error-message-dialog.component';
import { AbstractContentComponent } from '../../../../core/components/abstract/abstract-content/abstract-content.component';
import { DialogService } from '../../../../service/services/misc/dialog/dialog.service';
import { CreateOrganizationDialogComponent } from '../../dialogs/create-organization-dialog/create-organization-dialog.component';
import { TablePersonalizationService } from '../../../../service/services/misc/table-personalization/table-personalization.service';
import { SwaService } from '../../../../service/services/misc/swa/swa.service';
import { AppConfigService } from "../../../../service/services/misc/app-config/app-config.service";
import {SessionProviderService} from "../../../../service/services/misc/session-provider/session-provider.service";
import {UisService} from "../../../../service/services/misc/uis/uis.service";
import {Router} from "@angular/router";
import {EventService} from "../../../../service/services/misc/event/event.service";
import {ResourceModel} from "../../../../core/models/resources/resource.model";
import {OrganizationService} from "../../../../service/services/rest/organization/organization.service";
import {FolderService} from "../../../../service/services/rest/folder/folder.service";
import {ResourceGroupService} from "../../../../service/services/rest/resource-group/resource-group.service";
import {LocalStorageService} from "../../../../service/services/misc/local-storage/local-storage.service";
import {PersistencyData} from "../../../../shared/components/data-table/data-table.component";
import {FilterOnSearchPipe} from "../../../../shared/pipes/filter-on-search/filter-on-search.pipe";
import {ResourceDataService} from "../../../../service/services/rest/resource-data/resource-data.service";
import {TablePersonalizationDefinitions} from "../../../../core/helpers/TablePersonalizationDefinitions";

const noOrganizationMessage = $localize`:XMSG|Organization No Data message@@organizations.organization-content.noDataMessage:You don't have any organizations`;
const noOrganizationMessageFiltered = $localize`:XMSG|Organization No Data Filtered message@@organizations.organization-content.noDataMessageFiltered:No organizations match your search query`;
const initialSortProperty = 'displayName';
@Component({
    selector: 'app-organization-content',
    templateUrl: './organization-content.component.html',
    styleUrls: ['./organization-content.component.scss'],
    standalone: false
})
export class OrganizationContentComponent extends AbstractContentComponent implements OnChanges, OnInit {
    PAGE_SIZE = 100;
    Object = Object;
    Utils = Utils;
    override items: OrganizationModel[] = [];
    private savedSortProperty: string = initialSortProperty;
    private savedAscending: boolean = true;
    continueToken: string;
    displayLoadingText = "Loading...";
    @Input() isPaginatedView = false;
	@Output() isPaginatedViewChange = new EventEmitter<boolean>();

	@Input() selectedProject: string;
	@Input() searchTerm: string;
	@Input() advancedSearchTerms: any;
	@Input() override advancedSearchOpen = false;
	@Input() availableResourceTypes: any[] = [];
	@Input() currentFilterViewName: string = 'Standard';
	@Output() advancedSearchToggled = new EventEmitter<boolean>();
	@Output() errorEvent: EventEmitter<Error> = new EventEmitter<Error>();
	@Output() viewSaved: EventEmitter<string> = new EventEmitter<string>();
	@Output() filtersChanged = new EventEmitter<void>();
	NO_VALUE_LABEL = Const.NO_VALUE_LABEL;
	override noDataMessage = noOrganizationMessage;
	noDataMessageFiltered = noOrganizationMessageFiltered;

	scrollSubject = new Subject<void>();
	filterPipe: any;

	constructor(
		private _navigationService: NavigationService,
		private fileUploadService: FileUploadService,
		private notificationsService: NotificationsService,
		private organizationService: OrganizationService,
		protected override _dialogService: DialogService,
		private tablePersonalizationService: TablePersonalizationService,
		private sessionProvider: SessionProviderService,
		protected override swaService: SwaService,
		protected override appConfigService: AppConfigService,
		protected override uisService: UisService,
		protected override router: Router,
		private _eventService: EventService,
		private _localStorageService: LocalStorageService,
		private resourceDataService: ResourceDataService,
		private _folderService: FolderService,
		private _resourceGroupService: ResourceGroupService,
		private _aiAssistantService: AiAssistantService
	) {
  		super(fileUploadService, notificationsService, _dialogService, tablePersonalizationService, swaService, appConfigService, uisService, router);
	  	this.subscribeToTablePersonalizationChange();
	  	this.subscribeToNavigationStart();
	    this.sortProperty = initialSortProperty;
	}

	async ngOnChanges(changes: SimpleChanges) {
	    if (changes['advancedSearchOpen']) {
	        const isOpen = changes['advancedSearchOpen'].currentValue;

	        if (isOpen && !changes['advancedSearchOpen'].previousValue) {
	            this.savedSortProperty = this.sortProperty;
	            this.savedAscending = this.ascending;
	            this.handleAdvancedSearchOpened();
	        } else if (!isOpen && changes['advancedSearchOpen'].previousValue) {
	            this.handleAdvancedSearchClosed();
	        }
	    }

	    if (!this.isPaginatedView) {
	        return;
	    }
	    const searchTermChange = changes['searchTerm'];
	    const searchTerm = searchTermChange?.currentValue;
	    if (searchTerm !== undefined && !searchTermChange.firstChange) {
	        await this.loadContent(true);
	        this.noDataMessage = searchTerm ? noOrganizationMessageFiltered : noOrganizationMessage;
	    }
	}

	protected override handleAdvancedSearchClosed() {
	    super.handleAdvancedSearchClosed();
	    const persistedSort = JSON.parse(this._localStorageService.get(`${TablePersonalizationDefinitions.ResourcesTable.key}-sort`)) as PersistencyData;
	    this.sortProperty = persistedSort?.data?.prop ?? this.savedSortProperty;
	    this.ascending = persistedSort?.data?.asc ?? this.savedAscending;
	}

	async ngOnInit() {
	    this._eventService.emitPageLoadEvent("success");
	    const persistency = JSON.parse(this._localStorageService.get(`${this.currentTableKey}-sort`)) as PersistencyData;
	    if (persistency) {
	        this.sortProperty = persistency.data.prop;
	        this.ascending = persistency.data.asc;
	    }

	    try {
	        await this.loadContent(true);
	        const isPaginated = !!this.continueToken;
	        this.isPaginatedView = isPaginated;
	        this.isPaginatedViewChange.emit(isPaginated);
	        if (!isPaginated) {
	            this.filterPipe = FilterOnSearchPipe;
	        } else {
	            this.subscribeToScroll();
	        }

	    } finally {

	    }
	}

	subscribeToScroll() {
	    const scrollSubscription = this.scrollSubject.pipe(
	        debounceTime(200),
	        filter(() => !this.loading)
	    ).subscribe(async () => this.onScrollEvent());
	    this.subscriptions.push(scrollSubscription);
	}

	async onScrollEvent() {
	    // Don't paginate when advanced search is open
	    if (this.advancedSearchOpen) {
	        return;
	    }
	    const end = this.dataTable.tableViewPort.measureScrollOffset('bottom');
	    if (end < 200 && this.continueToken) {
	        await this.loadContent(false, this.continueToken);
	    }
	}

	async loadContent(reset: boolean, continueToken?: string) {
	    try {
	        this.loading = true;
	        const paginatedResponse = await firstValueFrom(this.organizationService
	            .getPage(this.PAGE_SIZE, this.searchTerm, this.sortProperty, this.ascending, continueToken));
	        if (reset) {
	            this.items = [];
	            this.dataTable.tableViewPort.scrollToIndex(0);
	        }
	        this.items = [...this.items, ...paginatedResponse.data];
	        this.continueToken = paginatedResponse.continueToken;
	    } catch(err: any) {
	        this.errorEvent.emit(err);
	    } finally {
	        this.loading = false;
	    }
	}

	async reset() {
	    if (!this.isPaginatedView) {
	        this.dataTable.reset();
	        return;
	    }
	    this.sortProperty = initialSortProperty;
	    this.ascending = true;
	    this.dataTable.reset();
	    await this.loadContent(true);
	}

	async onSortChange($event: any) {
	    // Nothing changed
	    if ($event.prop === this.sortProperty && $event.asc === this.ascending) {
	        return;
	    }
	    this.sortProperty = $event.prop;
	    this.ascending = $event.asc;
	    if (!this.isPaginatedView || this.advancedSearchOpen) {
	        return;
	    }
	    await this.loadContent(true);
	}

	onEditOrganization(organization: ResourceModel) {
	    // For non-org/folder/RG types the "edit" action means edit-with-YAML; delegate to onEditResource.
	    if (this.showEditWithYaml(organization)) {
	        this.onEditResource(organization);
	        return;
	    }
	    this.sessionProvider.validateSession();
	    this.uisService.sendUISEvent(this.uisService.UIS_EVENT_TYPES.OPEN_EDIT_RESOURCE_FORM + " - " + organization.type, {type: organization.type, path: organization.path});
	    this.swaService.sendCustomEvent(this.swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM, [organization.type], []);
	    const dialogType = this.getDialogType(organization.type);
	    let dialogRef = this._dialogService.openSideDialog(dialogType, {
	        resource: organization,
	        isEdit: true,
	    });
	    dialogRef.afterClosed.subscribe({
	        next: (res: OrganizationModel) => {
	            if (!res) return;
	            this.updateOrganization(res);
	            //Triggers change detection on content details
	            if (this.selectedRow?.name === res.name && this.selectedRow?.type === res.type) {
	                this.selectedRow = Object.assign({}, res);
	            }
	        },
	    });
	}

	updateOrganization(res: OrganizationModel) {
	    let item = this.items.find(
	        (item) => this.findSelectedRow(item, res)
	    );
	    if (item) {
	        Object.assign(item, res);
	    }
	    this.items = [...this.items];
	}

	getDialogType(resourceType: string) {
	    switch (resourceType) {
	    case ResourceType.FOLDER:
	        return CreateFolderDialogComponent;
	    case ResourceType.RESOURCE_GROUP:
	        return CreateResourceGroupDialogComponent;
	    default:
	        return CreateOrganizationDialogComponent;
	    }
	}

	override isEditable(row: ResourceModel): boolean {
	    if (row?.type === ResourceType.ORGANIZATION) {
	        return true;
	    }
	    const rules = row?.rules || [];
	    return rules.some(r => r.action === PolicyAction.Update || r.action === PolicyAction.All);
	}

	onEditInline(resource: ResourceModel) {
	    if (!this.showEditWithYaml(resource)) {
	        this.onEditOrganization(resource);
	    } else {
	        this.onEditResource(resource);
	    }
	}

	async onEditResource(resource: ResourceModel, schema?: any, isExpanded = false) {
	    this.sessionProvider.validateSession();
	    let freshResource: ResourceModel;
	    try {
	        const query = this.getGetResourceQueryString(resource);
	        const url = `${resource.apiVersion}/${resource.type}${resource.path}/${resource.name}`;
	        freshResource = await firstValueFrom(this.resourceDataService.get(url, query));
	        // Preserve rules and customSubresources from the policy-enriched resource;
	        // the HTTP GET endpoint does not return them.
	        freshResource.rules = resource.rules;
	        freshResource.customSubresources = resource.customSubresources;
	    } catch {
	        freshResource = resource;
	    }
	    this.selectedRow = freshResource;
	    const dialogType = this.showEditWithYaml(freshResource)
	        ? (schema ? CreateInstanceDialogComponent : CreateGenericResourceDialogComponent)
	        : CreateGenericResourceDialogComponent;
	    const dialogRef = this._dialogService.openSideDialog(
	        dialogType,
	        { resource: freshResource, schema, isEdit: true },
	        this.showEditWithYaml(freshResource) ? '62.5rem' : undefined
	    );
	    dialogRef.afterClosed.subscribe({
	        next: (res: ResourceModel) => {
	            if (!res) return;
	            this.updateOrganization(res as OrganizationModel);
	            if (this.selectedRow?.name === res.name && this.selectedRow?.type === res.type) {
	                this.selectedRow = Object.assign({}, res);
	            }
	        },
	    });
	}

	onEditWithForm(resource: ResourceModel) {
	    if (!this.showEditWithYaml(resource)) {
	        this.onEditOrganization(resource);
	        return;
	    }
	    const url = `${resource.apiVersion}/${resource.type}${resource.path}/${resource.name}/.schema`;
	    this.setLoading(true);
	    this.resourceDataService.get(url).subscribe({
	        next: (schema: any) => {
	            this.setLoading(false);
	            this.onEditResource(resource, schema);
	        },
	        error: (err: any) => {
	            this.errorEvent.emit(err);
	            this.setLoading(false);
	        },
	    });
	}

	async onReloadResource(resource: ResourceModel) {
	    try {
	        this.setLoading(true);
	        const query = this.getGetResourceQueryString(resource);
	        const url = `${resource.apiVersion}/${resource.type}${resource.path}/${resource.name}`;
	        const freshResource = await firstValueFrom(this.resourceDataService.get(url, query));
	        // Preserve rules and customSubresources — the GET endpoint does not return them.
	        freshResource.rules = resource.rules;
	        freshResource.customSubresources = resource.customSubresources;
	        this.updateOrganization(freshResource as OrganizationModel);
	        if (this.isFclClosed()) {
	            this.toggleFcl(freshResource);
	        } else {
	            this.selectedRow = freshResource;
	        }
	    } catch {
	    } finally {
	        this.setLoading(false);
	    }
	}

	onMoveResource(resourceToMove: ResourceModel) {
	    const moveDialogRef = this._dialogService.openDialog(MoveResourceDialogComponent, {resource: resourceToMove});
	    moveDialogRef.afterClosed.subscribe({
	        next: (moveDialogResult: any) => {
	            const moveConfirmationDialogRef = this._dialogService.openDialog(MoveResourceConfirmationDialogComponent, moveDialogResult);
	            moveConfirmationDialogRef.afterClosed.subscribe({
	                next: (result: any) => {
	                    if (!result.preflightSucceeded) {
	                        this._dialogService.openDialog(MoveFailedErrorMessageDialogComponent, result);
	                    }
	                }
	            });
	        }
	    });
	}

	async onApplyAdvancedFilters(filters: any) {
	    this.currentAdvancedSearchFilters = filters;
	    this.forkReqSubject.next(1);
	    this.loading = true;
	    this.items = [];
	    filters.limit = Const.MAX_ADVANCED_FILTER_RESULTS;

	    this.searchSubscription = this.resourceDataService.getFilteredResources(filters).pipe(
	        switchMap(async (filteredItems) => {
	            const enriched = filteredItems.map(item => this.organizationService.enrichOrganization(item as OrganizationModel));
	            const uniqGVTList = this.getUniqueGVTModels(filteredItems);
	            this.policiesLoading = true;
	            const policies = await firstValueFrom(this.resourceDataService.getPolicies('', uniqGVTList));
	            return { enriched, policies };
	        }),
	        takeUntil(this.forkReqSubject)
	    ).subscribe({
	        next: ({ enriched, policies }) => {
	            this.items = enriched;
	            this.applyPolicies(policies);
	            this.policiesLoading = false;
	            this.noDataMessage = 'No resources match your search query.';
	            this.loading = false;
	            this.searchSubscription = null;
	        },
	        error: (err: any) => {
	            this.errorEvent.emit(err);
	            this.loading = false;
	            this.policiesLoading = false;
	            this.searchSubscription = null;
	        }
	    });
	}

	private getUniqueGVTModels(items: ResourceModel[]): GVTModel[] {
	    const allGVTModels = items.flatMap(item => {
	        const group = item.apiVersion?.split('/')[0];
	        const models: GVTModel[] = [
	            new GVTModel({ group, type: '*/*' }),
	        ];
	        if (item.resource?.metadata?.mixedInto) {
	            const mixedIntoType = item.resource.metadata.mixedInto.type;
	            const mixedIntoGroup = item.resource.metadata.mixedInto.apiVersion.split('/')[0];
	            models.push(new GVTModel({ group, type: `${item.type}@${mixedIntoType}.${mixedIntoGroup}` }));
	        } else {
	            models.push(new GVTModel({ group, type: item.type }));
	        }
	        return models;
	    });
	    return Array.from(
	        new Set(allGVTModels.map(model => JSON.stringify(model)))
	    ).map(str => JSON.parse(str));
	}

	private applyPolicies(policies: RuleModel[]): void {
	    const policiesMap: { [gt: string]: RuleModel[] } = {};
	    policies.forEach(policy => {
	        const delimiter = policy.type.includes('@') ? '@' : '/';
	        const [baseType] = policy.type.split(delimiter);
	        const gt = `${policy.group}-${baseType}`;
	        policiesMap[gt] = policiesMap[gt] || [];
	        policiesMap[gt].push(policy);
	    });

	    this.items.forEach(item => {
	        if (!item.apiVersion) return;
	        if (item.type === ResourceType.ORGANIZATION || item.type === ResourceType.FOLDER || item.type === ResourceType.RESOURCE_GROUP) return;
	        const [group] = item.apiVersion.split('/');
	        const gt = `${group}-${item.type}`;
	        item.rules = [
	            ...(policiesMap[gt] || []),
	            ...(policiesMap[`${group}-*`] || []),
	            ...(policiesMap[`*-${item.type}`] || []),
	            ...(policiesMap['*-*'] || [])
	        ];
	    });
	    this.items = [...this.items];
	}

	onAnalyzeFailure(resource: ResourceModel) {
	    this._aiAssistantService.open(this._aiAssistantService.buildResourceContext(resource));
	}

	async onReconcileResource(resource: ResourceModel) {
	    try {
	        this.notificationsService.showMessageToast('Reconciling the resource now. Monitor the status for an update.');
	        switch (resource.type) {
	        case ResourceType.ORGANIZATION:
	            await firstValueFrom(this.organizationService.touchByPath(resource.path, resource.name, resource as OrganizationModel));
	            break;
	        case ResourceType.FOLDER:
	            await firstValueFrom(this._folderService.touchByPath(resource.path, resource.name, resource));
	            break;
	        case ResourceType.RESOURCE_GROUP:
	            await firstValueFrom(this._resourceGroupService.touchByPath(resource.path, resource.name, resource));
	            break;
	        default:
	            await firstValueFrom(this.resourceDataService.touch(resource.apiVersion, resource.type, resource.path, resource.name, resource));
	        }
	        resource.state = ResourceState.IN_PROCESS;
	        this.updateOrganization(resource as OrganizationModel);
	    } catch (e: Error | any) {
	        this._dialogService.openDialog(ReconcileFailedErrorMessageDialogComponent, {errorMsg: e.message});
	    }
	}

	async onDeleteResource(resource: ResourceModel) {
	    const data: any = { resource };

	    switch (resource.type) {
	    case ResourceType.FOLDER: {
	        const content = await this.getResourceContent(resource);
	        if (content.length > 0) {
	            this.handleHasContentDelete(ResourceType.FOLDER);
	            return;
	        }
	        data.title = 'Delete Folder';
	        data.deleteFunc = () => this._folderService.delete(resource.path, resource.name);
	        break;
	    }
	    case ResourceType.RESOURCE_GROUP: {
	        const content = await this.getResourceContent(resource);
	        if (content.length > 0) {
	            this.handleHasContentDelete(ResourceType.RESOURCE_GROUP);
	            return;
	        }
	        data.title = 'Delete Resource Group';
	        data.deleteFunc = () => this._resourceGroupService.delete(resource.path, resource.name);
	        break;
	    }
	    default: {
	        data.title = 'Delete ' + (resource.typeDisplayName || resource.type);
	        const path = resource.path || '';
	        const url = `/${resource.apiVersion}/${resource.type}/${path}`;
	        const queryString = this.getMixedIntoQueryString(resource);
	        data.deleteFunc = () => this.resourceDataService.delete(url, resource.name, queryString);
	        break;
	    }
	    }

	    const dialogRef = this._dialogService.openDialog(DeleteConfirmationDialogComponent, data);
	    dialogRef.afterClosed.subscribe({
	        next: (res: ResourceModel) => {
	            if (!res) return;
	            this.handlePollWhileExists(res);
	        }
	    });
	}

	async getResourceContent(resource: ResourceModel): Promise<any[]> {
	    try {
	        const path = resource.path + '/' + resource.name;
	        return await firstValueFrom(this.resourceDataService.getResourcesInPath(path, 'validateParent=false'));
	    } catch {
	        return [];
	    }
	}

	handlePollWhileExists(resource: ResourceModel) {
	    const item = this.items.find(i => this.findSelectedRow(i, resource));
	    if (!item) return;
	    item.state = ResourceState.TERMINATING;
	    if (this.findSelectedRow(this.selectedRow, item)) {
	        this.selectedRow = Object.assign({}, item);
	    }
	    // Remove item from table once confirmed deleted
	    setTimeout(() => {
	        this.items = this.items.filter(i => !this.findSelectedRow(i, resource));
	        this.items = [...this.items];
	        if (this.findSelectedRow(this.selectedRow, resource)) {
	            this.closeFcl();
	        }
	    }, 3000);
	}
}
